import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  IComunicadorAlarma,
  IConfigReenvio,
  IDispositivoAlarma,
  IQueryParam,
  IFilter,
  IPopulate,
  IGeoJSONPoint,
} from 'modelos/src';
// El helper es un valor en runtime, no un tipo: se importa del entry compilado
// del paquete ('modelos' -> dist/index.js). Importarlo de 'modelos/src' obliga a
// esbuild a resolver el barrel TypeScript dentro de node_modules, que no resuelve
// los imports de directorio sin extension y rompe el dev server.
import { nombreModoZonaUnicom } from 'modelos';
import { Subscription, firstValueFrom, retry, throwError, timer } from 'rxjs';
import { ModeloDispositivoSelectorCacheService } from '../../../../../../componentes/selectores/modelo-dispositivo-selector/modelo-dispositivo-selector-cache.service';
import {
  estadoComunicacionAlarma,
  estadoConexionAlarma,
  ultimaComunicacionAlarma,
} from '../../../../../../auxiliares/comunicadores-alarma.util';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';
import {
  configReenvioVigente,
  IReenviosTrackerData,
  ReenviosTrackerDialogComponent,
} from '../../../../administracion/trackers/reenvios-tracker-dialog/reenvios-tracker-dialog.component';
import { MarkerMapa } from '../../../../../../auxiliares/mapa-facil/interfaces';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { SimsService } from '../../../../../../auxiliares/servicios/http/sims.service';
import { PermisosEntidadesService } from '../../../../../../auxiliares/servicios/http/permisos-entidades.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { MapCardConfig } from '../../../../../../componentes/map-card/map-card.component';
import {
  SimDetalle,
  TonoEstado,
} from '../../../../../../componentes/detalle/detalle.components';
import {
  EstadoConexion,
  duracion,
  haceCuanto,
} from '../../../../../../auxiliares/estado-conexion.util';
import { TIPOS_SENSORES } from '../../alarmas-domiciliarias.service';

interface VistaComunicador {
  nombre: string;
  conexion: EstadoConexion;
  hace?: string;
  fecha?: string;
}

interface VistaZona {
  nombre: string;
  descripcion: string;
  sensores: string[];
}

interface VistaCamara {
  id: string;
  nombre: string;
  detalle: string;
  host?: string;
  ruta?: string[];
}

/** Lo que muestra la pestaña Info, ya armado para la plantilla. */
interface VistaAlarma {
  conexion: EstadoConexion;
  ultimaComunicacion?: string;
  hace?: string;
  armado?: string;
  activacion: { etiqueta: string; tono: TonoEstado; detalle?: string };
  marcaModelo?: string;
  numeroAbonado?: string;
  nroDeSistema?: string;
  fechaAlta?: string;
  fechaCreacion?: string;
  /** frecReporte legible: "5 min", "23 h 59 min", "1 d". */
  frecuencia?: string;
  idUnicoComunicador?: string;
  comunicadores: VistaComunicador[];
  /** Solo con permiso para ver reenvíos. */
  reenvios?: { activas: number; total: number };
  zonas: VistaZona[];
  sims: SimDetalle[];
  camaras: VistaCamara[];
  imagenes: string[];
}

@Component({
  selector: 'app-detalles-alarma-info',
  templateUrl: './detalles-alarma-info.component.html',
  styleUrl: './detalles-alarma-info.component.scss',
  standalone: false,
})
export class DetallesAlarmaInfoComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private route = inject(ActivatedRoute);
  private formularios = inject(FormularioDialogService);
  private modeloCache = inject(ModeloDispositivoSelectorCacheService);
  private simsService = inject(SimsService);
  private permisosEntidades = inject(PermisosEntidadesService);

  public loading = true;
  public id: string | null = null;
  /** comunicadores[] con `.modelo` resuelto por idComunicador (no depende
   * del populate `comunicador`, que solo trae el principal). */
  private comunicadoresResueltos: IComunicadorAlarma[] = [];
  public alarma?: IDispositivoAlarma;
  public alarmas$: Subscription;
  private comunicadores$?: Subscription;

  /// Reenvíos que aplican a esta alarma. null hasta que llegan: mientras
  /// tanto no se muestra "No hay activas".
  private configsReenvio: IConfigReenvio[] | null = null;
  private configsReenvioPedidas = false;
  private puedeVerReenvios = this.helper.tienePermiso(
    'Administración - Ver configuraciones de reenvíos',
  );

  public vista?: VistaAlarma;
  public mapCardConfig?: MapCardConfig;

  /**
   * Alto del mapa en px: el de su lugar en la grilla (las dos primeras
   * tarjetas). map-card le resta su encabezado al alto que recibe: con "100%"
   * lo restaría dos veces. Que Mapbox se redimensione lo resuelve map-card.
   */
  public pantallaAncha = signal(window.innerWidth > 1100);
  public altoMapa = signal<number | null>(null);
  private observadorMapa?: ResizeObserver;

  @ViewChild('tarjetaMapa') set tarjetaMapa(
    ref: ElementRef<HTMLElement> | undefined,
  ) {
    this.observadorMapa?.disconnect();
    if (!ref) return;
    this.observadorMapa = new ResizeObserver(([entrada]) => {
      this.altoMapa.set(Math.round(entrada.contentRect.height) || null);
    });
    this.observadorMapa.observe(ref.nativeElement);
  }

  @HostListener('window:resize')
  public alRedimensionar(): void {
    this.pantallaAncha.set(window.innerWidth > 1100);
  }

  /**
   * Armado: UNICOM tiene modo propio (total, perimetral, selectivo); el resto,
   * un booleano por partición (o uno solo, en alarmas viejas). Sin dato no se
   * inventa un "No".
   */
  private armado(a: IDispositivoAlarma): string | undefined {
    const modo = a.estadoArmadoUnicom?.modo;
    if (modo) {
      return modo === 'Desarmado' ? 'Desarmada' : `Armada (${modo})`;
    }
    const armado = a.armado as boolean[] | boolean | undefined;
    if (typeof armado === 'boolean') return armado ? 'Armada' : 'Desarmada';
    if (!Array.isArray(armado) || !armado.length) return undefined;
    const armadas = armado.filter((p) => p === true).length;
    if (!armadas) return 'Desarmada';
    if (armadas === armado.length) return 'Armada';
    return `Armada en ${armadas} de ${armado.length} particiones`;
  }

  /**
   * Activación: desactivada entera, en parte (particiones o zonas) o activa.
   * Una desactivación con fecha de fin vencida sigue desactivada mientras no
   * se limpie el flag (gestion-api-eventos mira el "desde" pero no el
   * "hasta"): se muestra así, aclarando que venció.
   */
  private activacion(a: IDispositivoAlarma): VistaAlarma['activacion'] {
    const md = a.modoDesactivado;
    if (md?.dispositivoDesactivado) {
      const ahora = Date.now();
      if (!md.permanente && md.desde && new Date(md.desde).getTime() > ahora) {
        return {
          etiqueta: 'Activada',
          tono: 'ok',
          detalle: `Se desactiva el ${HelperService.getFechaYHora(md.desde)}`,
        };
      }
      const vencida =
        !md.permanente && !!md.hasta && new Date(md.hasta).getTime() < ahora;
      return {
        etiqueta: 'Desactivada',
        tono: 'advertencia',
        detalle: md.permanente
          ? 'Sin fecha de fin'
          : md.hasta
            ? `${vencida ? 'Venció el' : 'Hasta el'} ${HelperService.getFechaYHora(md.hasta)}`
            : undefined,
      };
    }
    const particiones = md?.alarma?.particiones?.length ?? 0;
    const zonas = md?.alarma?.zonas?.length ?? 0;
    if (particiones || zonas) {
      return {
        etiqueta: 'Desactivada en parte',
        tono: 'advertencia',
        detalle: [
          particiones
            ? `${particiones} partici${particiones === 1 ? 'ón' : 'ones'}`
            : null,
          zonas ? `${zonas} zona${zonas === 1 ? '' : 's'}` : null,
        ]
          .filter(Boolean)
          .join(' y '),
      };
    }
    return { etiqueta: 'Activada', tono: 'ok' };
  }

  /**
   * Zonas de a 8, con flechas: un panel puede tener 32 o más y la tarjeta
   * llenaba la pestaña.
   */
  public readonly ZONAS_POR_PAGINA = 8;
  public paginaZonas = signal(0);

  public ultimaPaginaZonas(zonas: VistaZona[]): number {
    return Math.max(0, Math.ceil(zonas.length / this.ZONAS_POR_PAGINA) - 1);
  }

  /** La página actual; si las zonas cambiaron y ya no existe, la última. */
  private paginaZonasValida(zonas: VistaZona[]): number {
    return Math.min(this.paginaZonas(), this.ultimaPaginaZonas(zonas));
  }

  public zonasDePagina(zonas: VistaZona[]): VistaZona[] {
    const desde = this.paginaZonasValida(zonas) * this.ZONAS_POR_PAGINA;
    return zonas.slice(desde, desde + this.ZONAS_POR_PAGINA);
  }

  /** Lugares vacíos para que la última página ocupe lo mismo que las demás. */
  public huecosZonas(zonas: VistaZona[]): number[] {
    if (zonas.length <= this.ZONAS_POR_PAGINA) return [];
    const enPagina = this.zonasDePagina(zonas).length;
    return Array.from(
      { length: this.ZONAS_POR_PAGINA - enPagina },
      (_, i) => i,
    );
  }

  public rangoZonas(zonas: VistaZona[]): string {
    const desde = this.paginaZonasValida(zonas) * this.ZONAS_POR_PAGINA;
    const hasta = Math.min(desde + this.ZONAS_POR_PAGINA, zonas.length);
    return `${desde + 1}–${hasta} de ${zonas.length}`;
  }

  public moverZonas(paso: number, zonas: VistaZona[]): void {
    const pagina = this.paginaZonasValida(zonas) + paso;
    this.paginaZonas.set(
      Math.max(0, Math.min(pagina, this.ultimaPaginaZonas(zonas))),
    );
  }

  /** Ordenadas por partición y número de zona, no por orden de carga. */
  private zonas(a: IDispositivoAlarma): VistaZona[] {
    return [...(a.infoZonas ?? [])]
      .sort(
        (x, y) =>
          (x.particion ?? 0) - (y.particion ?? 0) ||
          (x.zona ?? 0) - (y.zona ?? 0),
      )
      .map((zona) => {
        // El modo real de un sensor RF UNICOM vive en `sensores[].modoRf`
        // ("R###"), no en el `modo` (enum viejo) de la zona. Si no está en
        // MODOS_ZONA_UNICOM mostramos el código crudo: es un dato real que el
        // operador puede reportar, mejor eso que inventar un nombre.
        const modoRf = zona.sensores?.[0]?.modoRf;
        const modo = modoRf
          ? (nombreModoZonaUnicom(modoRf) ?? modoRf)
          : (zona.modo ?? null);
        return {
          nombre: zona.nombre || 'Sin nombre',
          descripcion: [
            zona.particion != null ? `Partición ${zona.particion}` : null,
            zona.zona != null ? `Zona ${zona.zona}` : null,
            modo ? `Modo ${modo}` : null,
          ]
            .filter(Boolean)
            .join(' · '),
          sensores: (zona.sensores ?? []).map((s) =>
            [
              s.tipo
                ? (TIPOS_SENSORES[s.tipo as keyof typeof TIPOS_SENSORES] ??
                  s.tipo)
                : 'Sin tipo',
              s.marca,
              s.codigoRf ? `RF ${s.codigoRf}` : null,
            ]
              .filter(Boolean)
              .join(' · '),
          ),
        };
      });
  }

  /**
   * Arma la vista con lo que haya llegado. Se llama desde los tres caminos de
   * carga (alarma, modelos de los comunicadores y reenvíos), que terminan en
   * cualquier orden.
   */
  private armarVista(): void {
    const a = this.alarma;
    if (!a) return;

    // La comunicación de la alarma es la más reciente entre todos sus
    // comunicadores; el estado, con el criterio del módulo de alarmas
    // (comunicadores-alarma.util).
    const fecha = ultimaComunicacionAlarma(a);
    const verCamaras = this.permisosEntidades.verModuloCamaras();
    const configs = this.configsReenvio;

    this.vista = {
      conexion: estadoConexionAlarma(a),
      ultimaComunicacion: fecha
        ? HelperService.getFechaYHora(fecha)
        : undefined,
      hace: fecha ? haceCuanto(fecha) : undefined,
      armado: this.armado(a),
      activacion: this.activacion(a),
      marcaModelo:
        [a.modelo?.marca, a.modelo?.modelo].filter(Boolean).join(' · ') ||
        undefined,
      numeroAbonado: a.numeroAbonado,
      nroDeSistema: a.nroDeSistema,
      fechaAlta: a.fechaAlta
        ? HelperService.getFechaYHora(a.fechaAlta)
        : undefined,
      fechaCreacion: a.fechaCreacion
        ? HelperService.getFechaYHora(a.fechaCreacion)
        : undefined,
      frecuencia: a.frecReporte ? duracion(a.frecReporte) : undefined,
      idUnicoComunicador: a.idUnicoComunicador,
      // Cada comunicador con su estado: con un backup, el que no reporta no
      // queda tapado por el que sí. Las fechas salen de la alarma recién
      // llegada; de los resueltos solo el modelo, que puede tardar (o fallar)
      // sin dejar la lista vacía.
      comunicadores: (a.comunicadores ?? []).map((c, i) => {
        const modelo =
          this.comunicadoresResueltos.find(
            (r) => r.idComunicador && r.idComunicador === c.idComunicador,
          )?.modelo ?? c.modelo;
        return {
          nombre:
            [modelo?.marca, modelo?.modelo].filter(Boolean).join(' ') ||
            `Comunicador ${i + 1}`,
          conexion: estadoComunicacionAlarma(
            c.fechaUltimaComunicacion,
            a.frecReporte,
          ),
          hace: c.fechaUltimaComunicacion
            ? haceCuanto(c.fechaUltimaComunicacion)
            : undefined,
          fecha: c.fechaUltimaComunicacion
            ? HelperService.getFechaYHora(c.fechaUltimaComunicacion)
            : undefined,
        };
      }),
      reenvios:
        this.puedeVerReenvios && configs
          ? {
              activas: configs.filter((c) => configReenvioVigente(c)).length,
              total: configs.length,
            }
          : undefined,
      zonas: this.zonas(a),
      sims: [
        this.simsService.simDetalle(1, a.sim1, a.idSim1),
        this.simsService.simDetalle(2, a.sim2, a.idSim2),
      ],
      camaras: (a.camaras ?? []).map((c) => ({
        id: c._id,
        nombre: c.identificacion || 'Sin identificación',
        detalle: [
          [c.modeloDispositivo?.marca, c.modeloDispositivo?.modelo]
            .filter(Boolean)
            .join(' '),
          c.tipo,
          c.canales?.length
            ? `${c.canales.length} canal${c.canales.length === 1 ? '' : 'es'}`
            : null,
        ]
          .filter(Boolean)
          .join(' · '),
        host: c.host,
        ruta:
          verCamaras && c._id
            ? ['/main', 'camaras', 'detalles', c._id]
            : undefined,
      })),
      imagenes: a.imagenes ?? [],
    };
  }

  private prepararMapa(): void {
    const coordenadas = (this.alarma?.domicilio?.geojson as IGeoJSONPoint)
      ?.coordinates as [number, number] | undefined;
    if (!coordenadas) {
      this.mapCardConfig = undefined;
      return;
    }
    // Solo la primera vez: con el refresco no se le pisa el zoom ni el
    // encuadre al usuario (el domicilio no se mueve).
    if (this.mapCardConfig) return;
    const marker: MarkerMapa = {
      label: 'puntoSimple',
      coor: coordenadas,
      centrar: true,
    };
    // La dirección ya es el subtítulo del encabezado de la página.
    this.mapCardConfig = {
      title: 'Domicilio',
      icon: 'location_on',
      color: 'red',
      mapOptions: { markers: [marker], center: coordenadas, zoom: 17 },
    };
  }

  private listar(): Promise<void> {
    return new Promise((resolve) => {
      const filter: IFilter<IDispositivoAlarma> = { _id: this.id };
      const populate: IPopulate[] = [
        { path: 'cliente', select: 'nombre' },
        { path: 'modelo', select: 'modelo marca' },
        { path: 'domicilio', select: 'geojson direccion' },
        {
          path: 'camaras',
          select:
            'identificacion tipo host canales fechaCreacion idModeloDispositivo',
          populate: { path: 'modeloDispositivo', select: 'modelo marca' },
        },
        { path: 'sim1' },
        { path: 'sim2' },
      ];
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        populate: JSON.stringify(populate),
        includeChildren: true,
      };
      this.alarmas$?.unsubscribe();
      // Cada 30 s: la última comunicación y el armado cambian solos. Antes se
      // pedía una vez y la vista no se volvía a armar. Un request que falla
      // corta el timer de lista2: se reintenta a los 30 s en vez de dejar la
      // vista congelada sin avisar.
      this.alarmas$ = this.lista2
        .dispositivosAlarmas(query, 30)
        .pipe(
          retry({
            delay: (error) => {
              resolve();
              return this.alarma ? timer(30000) : throwError(() => error);
            },
          }),
        )
        .subscribe({
          next: (data) => {
            this.alarma = data?.datos?.[0];
            this.prepararMapa();
            this.resolverComunicadores();
            void this.listarConfigsReenvio();
            this.armarVista();
            resolve();
          },
          error: () => resolve(),
        });
    });
  }

  private resolverComunicadores(): void {
    this.comunicadores$?.unsubscribe();
    this.comunicadores$ = this.modeloCache
      .resolverModelosComunicadores(this.alarma?.comunicadores)
      .subscribe({
        next: (comunicadores) => {
          this.comunicadoresResueltos = comunicadores;
          this.armarVista();
        },
        // Sin modelos la lista queda con "Comunicador 1, 2…" y sus fechas.
        error: (error) =>
          console.error(
            'Error al resolver los modelos de comunicadores',
            error,
          ),
      });
  }

  /// Aplican a la alarma: las de la propia entidad y las de "todas las alarmas
  /// del cliente" (del cliente dueño, o de un ancestro si reenvía a hijos).
  private async listarConfigsReenvio(): Promise<void> {
    if (!this.puedeVerReenvios || this.configsReenvioPedidas) return;
    const idCliente = this.alarma?.idCliente;
    if (!idCliente) return;
    this.configsReenvioPedidas = true;

    const condiciones: IFilter<IConfigReenvio>[] = [
      { agrupacionReenvio: 'Entidad', idEntidadReenvio: this.id },
      {
        agrupacionReenvio: 'Todas las alarmas del cliente',
        idClienteReenvio: idCliente,
      },
    ];
    const ancestros = this.alarma?.idsAncestros ?? [];
    if (ancestros.length) {
      condiciones.push({
        agrupacionReenvio: 'Todas las alarmas del cliente',
        idClienteReenvio: { $in: ancestros },
        reenviarHijos: true,
      });
    }

    const populate: IPopulate[] = [
      { path: 'clienteReenvio', select: 'nombre' },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify({ $or: condiciones }),
      populate: JSON.stringify(populate),
      includeChildren: true,
      sort: '-fechaCreacion',
    };
    try {
      const data = await firstValueFrom(this.lista2.configReenvios(query));
      this.configsReenvio = data.datos;
      this.armarVista();
    } catch (error) {
      // Se vuelve a pedir con el próximo refresco de la alarma.
      this.configsReenvioPedidas = false;
      console.error('Error al listar configuraciones de reenvío', error);
    }
  }

  public verReenvios(): void {
    this.formularios.abrir<
      ReenviosTrackerDialogComponent,
      IReenviosTrackerData
    >(ReenviosTrackerDialogComponent, {
      tamano: 'mediano',
      cerrarAfuera: true,
      data: {
        nombreEntidad: this.alarma?.nombre ?? undefined,
        configs: this.configsReenvio ?? [],
      },
    });
  }

  async ngOnInit() {
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id');
    await this.listar();
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.observadorMapa?.disconnect();
    this.alarmas$?.unsubscribe();
    this.comunicadores$?.unsubscribe();
  }
}
